/* Team with Roles — Page redesign: compact layout → Team Simplified.dc.html, 1a
   (Member-first). The agency is the page header; Members gives a person one
   Role, the accounts it covers and any Exceptions; Roles edits the fleet's
   Roles once.

   A Role is a named permission set (CONTEXT.md: Role): what is enforced is
   still each person's grants per account; picking a Role also links the
   person to it there, and a person's Role, accounts and Exceptions are read
   off their grants and links (roleModel). Every access edit is a draft until
   Save, as on the classic Team page. Saving an edited Role updates everyone
   linked to it on the server; if some accounts fail, Team offers Re-apply.
   Structural actions (invite, remove, agency and account placement,
   new/delete Role) act at once behind their own buttons. */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  api,
  type Account,
  type AccountAssignment,
  type AgencyDetail,
  type AgencyRole,
  type AgencySummary,
  type AgencyTeamRow,
  type OwnedAccount,
  type TeamRole,
  type TeamRoleInput,
} from '../../../api/client';
import { useAuth } from '../../../context/AuthContext';
import { Btn, ErrorLine, Icon, Loading, Toggle } from '../../ChatAssistant/compact/kit';
import { C } from '../../ChatAssistant/compact/tokens';
import { font as f } from '../../ChatAssistant/compact/style';
import { Avatar, PickBox, SearchInput } from './AccessRow';
import { MemberDetail, type CopySource } from './MemberDetail';
import { initial, nameOf } from './people';
import { NO_ACCESS, crmKey, fromAssignment, isEmpty, sameAccess, setFans, type Access } from './permissionModel';
import { persistAccess, runPool } from './persistAccess';
import { RolesPane } from './RolesPane';
import {
  CUSTOM, NONE, accessOfRole, compileLinks, compilePlan, derivePlan, grantsOf, linkOfRef, refOf, sameGrants,
  withMain, withScope, type Plan, type PlanHint,
} from './roleModel';
import { AccountsMenu, Menu, SaveBar, type FleetAccount, type PoolAccount } from './TeamTab';
import './team.css';
import './teamRoles.css';

const PERSONAL = 'personal';

interface Person {
  user_id: string;
  email: string | null;
  username: string | null;
  /** Agency role; null = External; undefined = Personal fleet. */
  role?: AgencyRole | null;
  /** Accounts they hold access on (from the roster); 0 = nothing to load. */
  count: number;
}

const errMsg = (e: unknown, fallback: string) => (e instanceof Error && e.message) || fallback;
const matches = (q: string, ...fields: (string | null | undefined)[]) => {
  const needle = q.trim().toLowerCase();
  return !needle || fields.some((s) => s?.toLowerCase().includes(needle));
};
const pairKey = (uid: string, accountId: string) => `${uid}|${accountId}`;
const without = <T,>(rec: Record<string, T>, drop: (key: string) => boolean) =>
  Object.fromEntries(Object.entries(rec).filter(([k]) => !drop(k))) as Record<string, T>;
const accountName = (a: Account) => a.profile_name || a.username || a.account_id;
const isPending = (a: Account | undefined) => !!a && !a.username && !a.is_logged_in;
const sameInput = (a: TeamRoleInput, r: TeamRole) =>
  a.name === r.name && a.description === r.description && sameGrants(accessOfRole(a.grants), accessOfRole(r.grants));

export function TeamRolesTab({ onDirtyChange }: { onDirtyChange?: (dirty: boolean) => void } = {}) {
  const { t, i18n } = useTranslation('agency');
  const { user, isSuperAdmin } = useAuth();
  const myId = user?.id ?? '';

  /** Starting Roles come from the server in English; show them in the UI
      language until someone renames them. */
  const localise = useCallback((list: TeamRole[]) => {
    const en = i18n.getFixedT('en', 'agency');
    return list.map((r) => {
      if (!r.key) return r;
      const k = `ts.builtin.${r.key}`;
      return {
        ...r,
        name: r.name === en(`${k}.name`) ? t(`${k}.name`) : r.name,
        description: r.description === en(`${k}.desc`) ? t(`${k}.desc`) : r.description,
      };
    });
  }, [i18n, t]);

  const [agencies, setAgencies] = useState<AgencySummary[]>([]);
  const [owned, setOwned] = useState<OwnedAccount[]>([]);
  const [visible, setVisible] = useState<Account[]>([]);
  const [fleet, setFleet] = useState<string | null>(null);
  const [detail, setDetail] = useState<AgencyDetail | null>(null);
  const [people, setPeople] = useState<Person[]>([]);
  const [unsaved, setUnsaved] = useState<Person[]>([]); // Personal: invited, not granted yet
  const [assignmentsBy, setAssignmentsBy] = useState<Record<string, AccountAssignment[]>>({});
  const [roles, setRoles] = useState<TeamRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [fleetLoading, setFleetLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Drafts: grants (and the Role they follow) per person × account, agency
  // roles, Auto-placement, Role edits.
  const [draft, setDraft] = useState<Record<string, Access>>({});
  const [linkDraft, setLinkDraft] = useState<Record<string, string | null>>({});
  // Roles whose last save left some linked accounts behind: role id -> how many.
  const [behind, setBehind] = useState<Record<string, number>>({});
  const [roleDraft, setRoleDraft] = useState<Record<string, AgencyRole>>({});
  const [autoDraft, setAutoDraft] = useState<boolean | null>(null);
  const [roleEdits, setRoleEdits] = useState<Record<string, TeamRoleInput>>({});
  // "New role from …": a copy edited first and created on Save (a Role equal to
  // another one is refused, so a plain copy could never be saved).
  const [newRoles, setNewRoles] = useState<TeamRole[]>([]);
  const [hints, setHints] = useState<Record<string, PlanHint>>({});
  const [customOpen, setCustomOpen] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState<{ done: number; total: number } | null>(null);
  const [justSaved, setJustSaved] = useState(false);
  const [placing, setPlacing] = useState(false);
  // View
  const [tab, setTab] = useState<'members' | 'roles'>('members');
  const [sel, setSel] = useState<string | null>(null);
  const [roleSel, setRoleSel] = useState<string | null>(null);
  const [chip, setChip] = useState<string>('all');
  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [showDetail, setShowDetail] = useState(false); // phone: the detail replaces the list

  const agency = agencies.find((a) => a.id === fleet) ?? null;
  const isAgency = !!agency;
  const myRole: AgencyRole | null = agency?.role ?? null;
  const canGovern = isSuperAdmin || myRole === 'owner';
  const fleetArg = fleet === PERSONAL ? null : fleet;

  // ---------------------------------------------------------------- loading
  const loadBase = useCallback(async () => {
    const [ags, own, vis] = await Promise.all([api.getMyAgencies(), api.getMyOwnedAccounts(), api.getAccounts()]);
    setAgencies(ags);
    setOwned(own);
    setVisible(vis);
    return { ags, own };
  }, []);

  useEffect(() => {
    let live = true;
    loadBase()
      .then(({ ags, own }) => {
        if (!live) return;
        setFleet((cur) => cur ?? (own.length > 0 || isSuperAdmin || ags.length === 0 ? PERSONAL : ags[0]!.id));
      })
      .catch((e) => live && setError(errMsg(e, t('team.loadDataFailed'))))
      .finally(() => live && setLoading(false));
    return () => { live = false; };
  }, [loadBase, isSuperAdmin, t]);

  const fetchAssignments = useCallback((id: string, uid: string) =>
    (id === PERSONAL ? api.getMemberAccountAssignments(uid) : api.getAgencyMemberAccounts(id, uid)), []);

  /** Roster, Roles and everyone's grants (a person's Role is read off them). */
  const loadFleet = useCallback(async (id: string) => {
    const roleList = api.getTeamRoles(id === PERSONAL ? null : id);
    const grants = api.getFleetAssignments(id === PERSONAL ? null : id);
    let list: Person[];
    let d: AgencyDetail | null = null;
    if (id === PERSONAL) {
      const team = await api.getGlobalTeam();
      list = team.map((m) => ({ user_id: m.user_id, email: m.email, username: m.username ?? null, count: m.assigned_accounts_count }));
    } else {
      const [dd, roster] = await Promise.all([api.getAgencyDetail(id), api.getAgencyTeam(id)]);
      d = dd;
      list = roster.map((r: AgencyTeamRow) => ({ user_id: r.user_id, email: r.email, username: r.username, role: r.agency_role, count: r.assigned_accounts_count }));
    }
    // Only people holding something have grants to read (a super admin's
    // Personal roster is every user).
    const all = await grants;
    const by: Record<string, AccountAssignment[]> = {};
    for (const p of list) {
      if (!p.count) by[p.user_id] = [];
      else if (!(p.role === 'owner' || p.role === 'admin')) by[p.user_id] = all[p.user_id] ?? [];
    }
    setRoles(localise(await roleList));
    setDetail(d);
    setPeople(list);
    setAssignmentsBy((cur) => ({ ...cur, ...by }));
  }, [localise]);

  useEffect(() => {
    if (!fleet) return;
    let live = true;
    setFleetLoading(true);
    setSel(null);
    setRoleSel(null);
    setUnsaved([]);
    setAssignmentsBy({});
    setDraft({});
    setLinkDraft({});
    setBehind({});
    setRoleDraft({});
    setAutoDraft(null);
    setRoleEdits({});
    setNewRoles([]);
    setHints({});
    setCustomOpen(new Set());
    setPicked(new Set());
    setChip('all');
    setQuery('');
    setJustSaved(false);
    setShowDetail(false);
    loadFleet(fleet)
      .catch((e) => live && setError(errMsg(e, t('team.loadDataFailed'))))
      .finally(() => live && setFleetLoading(false));
    return () => { live = false; };
  }, [fleet, loadFleet, t]);

  const run = async (fn: () => Promise<unknown>, fallback: string) => {
    try { setError(null); setBusy(true); await fn(); return true; } catch (e) { setError(errMsg(e, fallback)); return false; } finally { setBusy(false); }
  };

  // ---------------------------------------------------------------- derived
  const byId = useMemo(() => new Map(visible.map((a) => [a.account_id, a])), [visible]);
  const accounts: FleetAccount[] = useMemo(() => {
    if (isAgency) return (detail?.accounts ?? []).map((a) => ({ account_id: a.account_id, name: a.name, owner_id: a.owner_id, agency_name: null }));
    if (isSuperAdmin) return visible.map((a) => ({ account_id: a.account_id, name: accountName(a), owner_id: null, agency_name: null }));
    return owned.map((a) => ({ account_id: a.account_id, name: a.name, owner_id: myId, agency_name: a.agency_name }));
  }, [isAgency, detail, isSuperAdmin, visible, owned, myId]);

  const roster: Person[] = useMemo(() => {
    const list = people.map((p) => (p.user_id in roleDraft ? { ...p, role: roleDraft[p.user_id] } : p));
    for (const u of unsaved) if (!list.some((p) => p.user_id === u.user_id)) list.unshift(u);
    const rank = (p: Person) => (p.role === 'owner' ? 0 : p.role === 'admin' ? 1 : 2);
    return isAgency ? [...list].sort((a, b) => rank(a) - rank(b)) : list;
  }, [people, roleDraft, unsaved, isAgency]);

  const isCo = useCallback((p: Person) => isAgency && (p.role === 'owner' || p.role === 'admin'), [isAgency]);
  /** Accounts a person can be given: never one they own (Agency fleet). */
  const editable = useCallback((uid: string) => accounts.filter((a) => !(isAgency && a.owner_id === uid)), [accounts, isAgency]);
  const savedAccessOf = useCallback((uid: string, accountId: string) =>
    fromAssignment((assignmentsBy[uid] ?? []).find((a) => a.account_id === accountId)), [assignmentsBy]);
  const accessOf = useCallback((uid: string, accountId: string) =>
    draft[pairKey(uid, accountId)] ?? savedAccessOf(uid, accountId), [draft, savedAccessOf]);
  /** The Role the saved grants follow (null = Custom or nothing). */
  const savedLinkOf = useCallback((uid: string, accountId: string) => {
    const row = (assignmentsBy[uid] ?? []).find((a) => a.account_id === accountId);
    return linkOfRef(refOf(fromAssignment(row), row?.role_id, roles));
  }, [assignmentsBy, roles]);
  const linkOf = useCallback((uid: string, accountId: string) => {
    const key = pairKey(uid, accountId);
    return key in linkDraft ? linkDraft[key]! : savedLinkOf(uid, accountId);
  }, [linkDraft, savedLinkOf]);
  const holds = (uid: string, accountId: string) => (assignmentsBy[uid] ?? []).some((a) => a.account_id === accountId);

  const planOf = useCallback((uid: string): Plan =>
    derivePlan(editable(uid).map((a) => a.account_id), (id) => accessOf(uid, id), (id) => linkOf(uid, id), roles, hints[uid]),
  [editable, accessOf, linkOf, roles, hints]);
  const plans = useMemo(() => new Map(roster.filter((p) => !isCo(p)).map((p) => [p.user_id, planOf(p.user_id)])), [roster, isCo, planOf]);

  const rolesView: TeamRole[] = useMemo(() => [...roles.map((r) => (roleEdits[r.id] ? { ...r, ...roleEdits[r.id] } : r)), ...newRoles], [roles, roleEdits, newRoles]);
  const roleName = (id: string) => (id === CUSTOM ? t('ts.custom') : id === NONE ? t('ts.noAccess') : roles.find((r) => r.id === id)?.name ?? id);
  const usedBy = (roleId: string) => [...plans.values()].filter((p) => p.ref === roleId).length;

  const heldCount = (uid: string) => editable(uid).filter((a) => !isEmpty(accessOf(uid, a.account_id))).length;
  /** The person's main Role as a badge: a linked Role (or co-manager role) is solid, Custom is outlined. */
  const badgeOf = (p: Person): { label: string; custom: boolean } | null => {
    if (isCo(p)) return { label: t(`fleet.role.${p.role}`), custom: false };
    const ref = plans.get(p.user_id)!.ref;
    return ref === NONE ? null : { label: roleName(ref), custom: ref === CUSTOM };
  };
  const scopeOf = (p: Person): string => {
    if (isCo(p)) return t('fleet.allAccounts');
    const plan = plans.get(p.user_id)!;
    if (plan.ref === NONE) return t('ts.needsSetupSub');
    const n = heldCount(p.user_id);
    const scope = plan.scope === 'all' && n === editable(p.user_id).length ? t('ts.allAccountsN', { count: n }) : t('fleet.accountCount', { count: n });
    const ex = plan.exceptions.filter((e) => !(e.follows && !e.access.fans.length)).length;
    return [scope, ex ? t('ts.exceptionCount', { count: ex }) : null].filter(Boolean).join(' · ');
  };
  const subOf = (p: Person): string => [badgeOf(p)?.label, scopeOf(p)].filter(Boolean).join(' · ');
  const needsSetup = (p: Person) => !isCo(p) && plans.get(p.user_id)?.ref === NONE;

  const chips = [
    { id: 'all', label: t('ts.everyone'), n: roster.length },
    ...roles.map((r) => ({ id: r.id, label: r.name, n: usedBy(r.id) })),
    ...(usedBy(CUSTOM) ? [{ id: CUSTOM, label: t('ts.custom'), n: usedBy(CUSTOM) }] : []),
    { id: NONE, label: t('ts.needsSetup'), n: roster.filter(needsSetup).length, warn: true },
  ];
  const shown = roster.filter((p) => {
    if (!matches(query, p.username, p.email)) return false;
    if (chip === 'all') return true;
    if (isCo(p)) return false;
    return plans.get(p.user_id)?.ref === chip;
  });

  const selected = roster.find((p) => p.user_id === sel) ?? roster.find((p) => p.user_id !== myId && !isCo(p)) ?? roster[0] ?? null;

  // ---------------------------------------------------------------- staging
  const dirtyCount = Object.keys(draft).length + Object.keys(roleDraft).length + Object.keys(roleEdits).length + newRoles.length + (autoDraft === null ? 0 : 1);
  const dirty = dirtyCount > 0;
  useEffect(() => { if (dirty) setJustSaved(false); }, [dirty]);
  useEffect(() => { onDirtyChange?.(dirty); }, [dirty, onDirtyChange]);
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  /** Stage per-account grants and the Role each follows for one person
      (dropping drafts equal to what is saved). */
  const stageGrants = (uid: string, next: Map<string, Access>, links: Map<string, string | null>) => {
    const unchanged = (id: string, access: Access) =>
      sameAccess(access, savedAccessOf(uid, id)) && (links.get(id) ?? null) === savedLinkOf(uid, id);
    setDraft((cur) => {
      const out = { ...cur };
      for (const [id, access] of next) {
        const key = pairKey(uid, id);
        if (unchanged(id, access)) delete out[key]; else out[key] = access;
      }
      return out;
    });
    setLinkDraft((cur) => {
      const out = { ...cur };
      for (const [id, access] of next) {
        const key = pairKey(uid, id);
        if (unchanged(id, access)) delete out[key]; else out[key] = links.get(id) ?? null;
      }
      return out;
    });
  };

  const stagePlan = (uid: string, plan: Plan) => {
    const ids = editable(uid).map((a) => a.account_id);
    stageGrants(uid, compilePlan(ids, plan), compileLinks(ids, plan));
    setHints((cur) => ({ ...cur, [uid]: { scope: plan.scope, main: plan.main, ref: plan.ref } }));
  };

  /** One Role (or No access) wherever the person has access now — on every account if nowhere yet. */
  const stageRoleFor = (uid: string, roleId: string) => {
    const plan = planOf(uid);
    if (roleId === NONE) { stagePlan(uid, { ...plan, main: NO_ACCESS, ids: [], exceptions: [] }); return; }
    const role = roles.find((r) => r.id === roleId);
    if (!role) return;
    const base = isEmpty(plan.main) ? withScope({ ...plan, scope: 'some' }, 'all', editable(uid).map((a) => a.account_id)) : plan;
    stagePlan(uid, withMain(base, accessOfRole(role.grants), role.id));
    setCustomOpen((cur) => { const n = new Set(cur); n.delete(uid); return n; });
  };

  /** Copy access from: the source's grants on every account both can be given. */
  const copyAccess = (from: string, to: string) => {
    const next = new Map(editable(to).map((a) => [a.account_id, accessOf(from, a.account_id)]));
    const links = new Map(editable(to).map((a) => [a.account_id, linkOf(from, a.account_id)]));
    stageGrants(to, next, links);
    setHints((cur) => without(cur, (k) => k === to));
  };

  const stageAgencyRole = (p: Person, role: AgencyRole) => {
    const saved = people.find((x) => x.user_id === p.user_id)?.role;
    setRoleDraft((cur) => (role === saved ? without(cur, (k) => k === p.user_id) : { ...cur, [p.user_id]: role }));
    if (role === 'admin') {
      setDraft((cur) => without(cur, (k) => k.startsWith(`${p.user_id}|`)));
      setLinkDraft((cur) => without(cur, (k) => k.startsWith(`${p.user_id}|`)));
    }
  };

  const editRole = (roleId: string, input: TeamRoleInput) => {
    if (newRoles.some((r) => r.id === roleId)) { setNewRoles((cur) => cur.map((r) => (r.id === roleId ? { ...r, ...input } : r))); return; }
    const saved = roles.find((r) => r.id === roleId);
    setRoleEdits((cur) => (saved && sameInput(input, saved) ? without(cur, (k) => k === roleId) : { ...cur, [roleId]: input }));
  };

  // ---------------------------------------------------------------- structural actions
  const refreshRoster = async (extra: string[] = []) => {
    if (!fleet) return;
    await loadFleet(fleet);
    for (const uid of extra) {
      const rows = await fetchAssignments(fleet, uid).catch(() => []);
      setAssignmentsBy((cur) => ({ ...cur, [uid]: rows }));
    }
  };

  const invite = async (identifier: string, roleId: string) => {
    if (!fleet) return;
    await run(async () => {
      let uid: string;
      if (fleet === PERSONAL) {
        const r = await api.addGlobalTeamMember(identifier);
        uid = r.user_id;
        if (!people.some((p) => p.user_id === uid)) {
          setUnsaved((cur) => [{ user_id: uid, email: r.email, username: r.username ?? null, count: 0 }, ...cur.filter((p) => p.user_id !== uid)]);
          setAssignmentsBy((cur) => ({ ...cur, [uid]: cur[uid] ?? [] }));
        }
      } else {
        const r = await api.addAgencyMemberByIdentifier(fleet, identifier, 'member');
        uid = r.user_id;
        await refreshRoster([uid]);
      }
      setSel(uid);
      setChip('all');
      setShowDetail(true);
      if (roleId !== NONE) stageRoleFor(uid, roleId);
    }, t('team.addTeamMemberFailed'));
  };

  const dropPerson = (uid: string) => {
    setDraft((cur) => without(cur, (k) => k.startsWith(`${uid}|`)));
    setLinkDraft((cur) => without(cur, (k) => k.startsWith(`${uid}|`)));
    setRoleDraft((cur) => without(cur, (k) => k === uid));
    setHints((cur) => without(cur, (k) => k === uid));
    setUnsaved((cur) => cur.filter((p) => p.user_id !== uid));
  };

  const removeOne = async (p: Person) => {
    if (fleet === PERSONAL && unsaved.some((u) => u.user_id === p.user_id) && !people.some((x) => x.user_id === p.user_id)) { dropPerson(p.user_id); return; }
    if (isAgency && fleet) await api.removeAgencyMember(fleet, p.user_id);
    else await api.removeGlobalTeamMember(p.user_id);
    dropPerson(p.user_id);
  };

  const removePeople = (list: Person[]) => {
    if (!list.length) return;
    const msg = list.length === 1
      ? (isAgency ? t('fleet.confirmRemove', { name: nameOf(list[0]!), agency: agency?.name, count: accounts.length }) : t('team.removeMemberConfirm', { email: list[0]!.email ?? nameOf(list[0]!) }))
      : t('ts.bulkRemoveConfirm', { count: list.length });
    if (!confirm(msg)) return;
    void run(async () => {
      for (const p of list) await removeOne(p);
      setPicked(new Set());
      setSel(null);
      await refreshRoster();
    }, t('team.removeTeamMemberFailed'));
  };

  const makeMember = (p: Person) => fleet && run(async () => {
    await api.addAgencyMemberByIdentifier(fleet, p.email || p.username || '', 'member');
    await refreshRoster();
  }, t('team.addTeamMemberFailed'));

  const makeOwner = (p: Person) => {
    if (!agency || !confirm(t('fleet.confirmTransfer', { name: nameOf(p), agency: agency.name }))) return;
    void run(async () => { await api.transferAgencyOwnership(agency.id, p.user_id); await loadBase(); await refreshRoster(); }, t('fleet.errors.transfer'));
  };

  const createRole = async (name: string, description: string, access: Access) => {
    const base = name.trim();
    if (!base) return null;
    let created: TeamRole | null = null;
    await run(async () => {
      created = await api.createTeamRole(fleetArg, { name: base, description, grants: grantsOf(access) });
      setRoles(localise(await api.getTeamRoles(fleetArg)));
    }, t('ts.errors.createRole'));
    return created as TeamRole | null;
  };

  const newRoleFrom = (role: TeamRole) => {
    const id = `new-${Date.now()}`;
    setNewRoles((cur) => [...cur, { id, key: null, name: t('ts.copyName', { name: role.name }), description: role.description, grants: role.grants, builtin: false, locked: false }]);
    setRoleSel(id);
  };

  const saveAsRole = async (uid: string, access: Access) => {
    const p = roster.find((x) => x.user_id === uid);
    const name = prompt(t('ts.newRoleName'), t('ts.roleFromPerson', { name: p ? nameOf(p) : '' }));
    if (!name) return;
    const r = await createRole(name, t('ts.savedFrom', { name: p ? nameOf(p) : '' }), access);
    if (r) setCustomOpen((cur) => { const n = new Set(cur); n.delete(uid); return n; });
  };

  const deleteRole = (role: TeamRole) => {
    if (newRoles.some((r) => r.id === role.id)) { setNewRoles((cur) => cur.filter((r) => r.id !== role.id)); setRoleSel(null); return; }
    if (!confirm(t('ts.deleteConfirm', { name: role.name, count: usedBy(role.id) }))) return;
    void run(async () => {
      await api.deleteTeamRole(fleetArg, role.id);
      setRoleEdits((cur) => without(cur, (k) => k === role.id));
      setRoles(localise(await api.getTeamRoles(fleetArg)));
      setRoleSel(null);
    }, t('ts.errors.deleteRole'));
  };

  // Agency fleet: accounts and the agency itself.
  const pool: PoolAccount[] = useMemo(() => {
    if (!isAgency) return [];
    const inHere = new Set((detail?.accounts ?? []).map((a) => a.account_id));
    const ownedById = new Map(owned.map((o) => [o.account_id, o]));
    const source = isSuperAdmin
      ? visible.map((a) => ({ account_id: a.account_id, name: accountName(a), agency_name: ownedById.get(a.account_id)?.agency_name ?? null }))
      : owned.map((o) => ({ account_id: o.account_id, name: o.name, agency_name: o.agency_name }));
    return source.filter((a) => !inHere.has(a.account_id))
      .map((a) => ({ ...a, pending: isPending(byId.get(a.account_id)) }))
      .sort((x, y) => Number(x.pending) - Number(y.pending) || x.name.localeCompare(y.name));
  }, [isAgency, detail, owned, isSuperAdmin, visible, byId]);

  const placeAccounts = async (ids: string[]) => {
    if (!fleet || !ids.length) return;
    setPlacing(true);
    await run(async () => {
      let failed = 0;
      let last: unknown = null;
      for (const id of ids) {
        try { await api.placeAccountInAgency(fleet, id); } catch (e) { failed++; last = e; }
      }
      await loadBase();
      await refreshRoster();
      if (failed === ids.length) throw last;
      if (failed) throw new Error(t('fleet.errors.placeSome', { count: failed, total: ids.length }));
    }, t('fleet.errors.place'));
    setPlacing(false);
  };

  const detach = (acc: FleetAccount, where: string) => {
    if (!confirm(t('fleet.confirmDetach', { account: acc.name, agency: where }))) return;
    void run(async () => {
      await api.detachAccountFromAgency(acc.account_id);
      if (isAgency) {
        setDraft((cur) => without(cur, (k) => k.endsWith(`|${acc.account_id}`)));
        setLinkDraft((cur) => without(cur, (k) => k.endsWith(`|${acc.account_id}`)));
      }
      await loadBase();
      await refreshRoster();
    }, t('fleet.errors.detach'));
  };

  const switchFleet = (id: string) => {
    if (id === fleet) return;
    if (dirty && !confirm(t('fleet.save.discardConfirm'))) return;
    setFleet(id);
  };

  const createAgency = async (name: string) => {
    if (dirty && !confirm(t('fleet.save.discardConfirm'))) return;
    let created: { id: string } | null = null;
    const ok = await run(async () => { created = await api.createAgency(name); await loadBase(); }, t('fleet.errors.create'));
    if (ok && created) setFleet((created as { id: string }).id);
  };

  const deleteAgency = async () => {
    if (!agency || !confirm(t('fleet.confirmDelete', { name: agency.name }))) return;
    const ok = await run(async () => { await api.deleteAgency(agency.id); await loadBase(); }, t('fleet.errors.delete'));
    if (ok) setFleet(owned.length > 0 || isSuperAdmin ? PERSONAL : (agencies.find((a) => a.id !== agency.id)?.id ?? PERSONAL));
  };

  // ---------------------------------------------------------------- save / discard
  const discard = () => {
    setDraft({});
    setLinkDraft({});
    setRoleDraft({});
    setAutoDraft(null);
    setRoleEdits({});
    setNewRoles([]);
    setHints({});
    setCustomOpen(new Set());
    setUnsaved([]);
  };

  /** People linked to a Role now (saved links), across the fleet. */
  const followers = (roleId: string) => new Set(roster.filter((p) => !isCo(p))
    .filter((p) => editable(p.user_id).some((a) => savedLinkOf(p.user_id, a.account_id) === roleId))
    .map((p) => p.user_id)).size;

  const save = async () => {
    if (!fleet || !dirty || saving) return;
    // An edited Role updates everyone linked to it (on the server): say so first.
    const roleJobs = Object.entries(roleEdits);
    for (const [roleId, input] of roleJobs) {
      const old = roles.find((r) => r.id === roleId);
      if (!old || sameGrants(accessOfRole(old.grants), accessOfRole(input.grants))) continue;
      const count = followers(roleId);
      if (count && !confirm(t('ts.updatePeopleConfirm', { count, name: input.name.trim() || old.name }))) return;
    }

    const accessJobs = Object.entries(draft).map(([key, next]) => {
      const [uid, accountId] = key.split('|') as [string, string];
      return { key, uid, accountId, next, roleId: linkDraft[key] ?? null };
    });
    const agencyRoleJobs = Object.entries(roleDraft);
    const autoJob = autoDraft;
    const createJobs = newRoles;
    const total = createJobs.length + roleJobs.length + agencyRoleJobs.length + (autoJob === null ? 0 : 1) + accessJobs.length;
    let failed = 0;
    let lastError: unknown = null;
    const tick = () => setSaving((s) => s && { ...s, done: s.done + 1 });
    setError(null);
    setJustSaved(false);
    setSaving({ done: 0, total });

    const created: string[] = [];
    for (const r of createJobs) {
      try { await api.createTeamRole(fleetArg, { name: r.name, description: r.description, grants: r.grants }); created.push(r.id); } catch (e) { failed++; lastError = e; }
      tick();
    }
    const savedRoles: string[] = [];
    const lagging: Record<string, number> = {};
    for (const [roleId, input] of roleJobs) {
      try {
        const res = await api.updateTeamRole(fleetArg, roleId, input);
        savedRoles.push(roleId);
        if (res.sync?.failed) lagging[roleId] = res.sync.failed;
      } catch (e) { failed++; lastError = e; }
      tick();
    }

    const savedAgencyRoles: string[] = [];
    for (const [uid, role] of agencyRoleJobs) {
      try { await api.setAgencyMemberRole(fleet, uid, role); savedAgencyRoles.push(uid); } catch (e) { failed++; lastError = e; }
      tick();
    }
    let autoSaved = false;
    if (autoJob !== null && agency) {
      try { await api.setAgencyAutoPlacement(agency.id, autoJob); autoSaved = true; } catch (e) { failed++; lastError = e; }
      tick();
    }
    // Someone given a Role that was edited in this same save gets its new version.
    const following = (j: (typeof accessJobs)[number]): Access => {
      const edited = j.roleId && savedRoles.includes(j.roleId) ? roleEdits[j.roleId] : undefined;
      if (!edited) return j.next;
      const role = accessOfRole(edited.grants);
      return setFans(role, role.has.has(crmKey('chats')) ? j.next.fans : []);
    };
    const jobs = accessJobs;
    const landed = new Set<string>();
    const errors = await runPool(jobs, async (j) => {
      await persistAccess(fleetArg, j.uid, j.accountId, following(j), holds(j.uid, j.accountId), j.roleId);
      landed.add(j.key);
    }, tick);
    failed += errors.length;
    if (errors.length) lastError = errors[0];

    try {
      if (autoSaved || savedAgencyRoles.length) await loadBase();
      await loadFleet(fleet);
      const touched = [...new Set(jobs.filter((j) => landed.has(j.key)).map((j) => j.uid))];
      for (const uid of touched.filter((u) => unsaved.some((p) => p.user_id === u))) {
        const rows = await fetchAssignments(fleet, uid);
        setAssignmentsBy((cur) => ({ ...cur, [uid]: rows }));
      }
    } catch (e) {
      failed++; lastError = e;
    }
    const wrote = new Map(accessJobs.filter((j) => landed.has(j.key)).map((j) => [j.key, j.next]));
    setDraft((cur) => without(cur, (k) => wrote.has(k) && wrote.get(k) === cur[k]));
    setLinkDraft((cur) => without(cur, (k) => wrote.has(k) && cur[k] === (linkDraft[k] ?? null)));
    setRoleDraft((cur) => without(cur, (k) => savedAgencyRoles.includes(k) && cur[k] === roleDraft[k]));
    setRoleEdits((cur) => without(cur, (k) => savedRoles.includes(k) && cur[k] === roleEdits[k]));
    setNewRoles((cur) => cur.filter((r) => !created.includes(r.id)));
    if (created.includes(roleSel ?? '')) setRoleSel(null);
    if (autoSaved) setAutoDraft((cur) => (cur === autoJob ? null : cur));
    setUnsaved((cur) => cur.filter((p) => !jobs.some((j) => j.uid === p.user_id && landed.has(j.key) && !isEmpty(j.next))));
    setBehind((cur) => ({ ...without(cur, (k) => savedRoles.includes(k)), ...lagging }));
    setSaving(null);

    if (!failed) setJustSaved(true);
    else if (failed >= total) setError(errMsg(lastError, t('team.savePermissionsFailed')));
    else setError(t('fleet.bulk.someFailed', { count: failed, total }));
  };

  /** Bring a Role's linked people back in line after a partly failed save. */
  const reapply = (roleId: string) => fleet && run(async () => {
    const res = await api.reapplyTeamRole(fleetArg, roleId);
    setBehind((cur) => (res.failed ? { ...cur, [roleId]: res.failed } : without(cur, (k) => k === roleId)));
    await loadFleet(fleet);
  }, t('team.savePermissionsFailed'));

  // ---------------------------------------------------------------- render
  if (loading) return <div className="cpt-root"><Loading /></div>;

  const fleetName = agency ? agency.name : t('fleet.personal');
  const showPersonal = owned.length > 0 || isSuperAdmin;
  const autoPlacement = autoDraft ?? !!agency?.auto_placement;
  const dirtyPeople = new Set([...Object.keys(draft).map((k) => k.split('|')[0]!), ...Object.keys(roleDraft)]);
  const pickable = shown.filter((p) => !isCo(p));
  const allPicked = pickable.length > 0 && pickable.every((p) => picked.has(p.user_id));
  const pickedPeople = roster.filter((p) => picked.has(p.user_id));
  const copySources = (exclude: Set<string>): CopySource[] => roster
    .filter((p) => !exclude.has(p.user_id) && !isCo(p))
    .map((p) => ({ user_id: p.user_id, name: nameOf(p), sub: subOf(p) }));
  const selectedRole = rolesView.find((r) => r.id === roleSel) ?? rolesView.find((r) => !r.locked) ?? rolesView[0];

  const canRemove = (p: Person) => {
    if (p.user_id === myId) return false;
    if (!isAgency) return true;
    if (p.role === 'owner') return false;
    return canGovern || p.role !== 'admin';
  };

  const openPerson = (uid: string) => { setSel(uid); setPicked(new Set()); setShowDetail(true); };

  const detailFor = (p: Person) => {
    const uid = p.user_id;
    const plan = plans.get(uid);
    const ownedHere = isAgency ? accounts.filter((a) => a.owner_id === uid).length : 0;
    return (
      <MemberDetail
        key={uid}
        name={nameOf(p)}
        email={p.email}
        plan={plan ?? derivePlan([], () => NO_ACCESS, () => null, roles)}
        roles={roles}
        accounts={editable(uid)}
        byId={byId}
        customOpen={customOpen.has(uid)}
        onCustomOpen={(open) => setCustomOpen((cur) => { const n = new Set(cur); if (open) n.add(uid); else n.delete(uid); return n; })}
        onPlan={(next) => stagePlan(uid, next)}
        roleControl={isAgency ? (
          <AgencyRoleControl person={p} myId={myId} myRole={myRole} canGovern={canGovern}
            onRole={(r) => stageAgencyRole(p, r)} onMakeMember={() => void makeMember(p)} onMakeOwner={() => makeOwner(p)} />
        ) : uid === myId ? <span className="tm-role" style={{ color: C.purpleLt }}>{t('fleet.you')}</span> : null}
        coManagerNote={isCo(p) ? t('ts.coManagerNote', { role: t(`fleet.role.${p.role}`), agency: fleetName }) : null}
        ownedNote={ownedHere ? t('ts.ownedNote', { count: ownedHere }) : null}
        copySources={copySources(new Set([uid]))}
        onCopyFrom={(from) => copyAccess(from, uid)}
        onRemove={canRemove(p) ? () => removePeople([p]) : null}
        onEditRole={(roleId) => { setRoleSel(roleId); setTab('roles'); }}
        onSaveAsRole={(access) => void saveAsRole(uid, access)}
        onBack={() => setShowDetail(false)}
      />
    );
  };

  return (
    <div className="cpt-root tm-root">
      <div className="tm-frame ts-frame">
        <AgencyBar
          name={fleetName}
          label={isAgency ? t('ts.agency') : t('ts.personalFleet')}
          role={agency ? (agency.role ? t(`fleet.role.${agency.role}`) : t('fleet.role.superAdmin')) : null}
          meta={`${t('fleet.memberCount', { count: roster.length })} · ${t('fleet.accountCount', { count: accounts.length })}`}
          accountsMenu={!fleetLoading && (isAgency || accounts.some((a) => a.agency_name)) ? (
            <AccountsMenu accounts={accounts} byId={byId} isAgency={isAgency} agencyName={agency?.name ?? null}
              pool={pool} anyAccount={isSuperAdmin} busy={placing} onAdd={(ids) => void placeAccounts(ids)} onDetach={detach} />
          ) : null}
          more={isAgency && (myRole === 'owner' || myRole === 'admin' || canGovern) ? (
            <MoreMenu autoPlacement={myRole === 'owner' || myRole === 'admin' ? autoPlacement : null}
              onAutoPlacement={(on) => setAutoDraft(on === !!agency?.auto_placement ? null : on)}
              onDelete={canGovern ? () => void deleteAgency() : null} agencyName={fleetName} />
          ) : null}
          switcher={
            <SwitchAgency current={fleet} agencies={agencies} showPersonal={showPersonal}
              personalMeta={t('fleet.accountCount', { count: isSuperAdmin ? visible.length : owned.length })}
              onPick={switchFleet} onCreate={(name) => void createAgency(name)} />
          }
        />

        <div className="ts-tabs">
          {([['members', t('ts.members'), roster.length, 'users'], ['roles', t('ts.roles'), roles.length, 'shield']] as const).map(([id, label, n, icon]) => (
            <button key={id} type="button" onClick={() => setTab(id)} className="ts-tab"
              style={{ color: tab === id ? C.text : C.sub, borderBottomColor: tab === id ? C.purple : 'transparent' }}>
              <Icon name={icon} size={14} />{label}<span style={{ font: f(500, 11.5), color: C.muted }}>{n}</span>
            </button>
          ))}
        </div>

        {error && <ErrorLine>{error}</ErrorLine>}
        {Object.entries(behind).map(([roleId, count]) => (
          <ErrorLine key={roleId}>
            <span style={{ flex: 1, minWidth: 0 }}>{t('ts.syncFailed', { count, name: roles.find((r) => r.id === roleId)?.name ?? roleId })}</span>
            <Btn kind="tint" height={26} icon="refresh-cw" disabled={busy} onClick={() => void reapply(roleId)}>{t('ts.reapply')}</Btn>
          </ErrorLine>
        ))}

        {fleetLoading ? <Loading /> : tab === 'roles' ? (
          selectedRole && (
            <RolesPane roles={rolesView} selected={selectedRole} onSelect={setRoleSel} usedBy={usedBy} fleetName={fleetName}
              dirty={new Set([...Object.keys(roleEdits), ...newRoles.map((r) => r.id)])} busy={busy}
              onEdit={editRole} onCreateFrom={newRoleFrom} onDelete={deleteRole} />
          )
        ) : (
          <div className={`ts-members${showDetail ? ' show-detail' : ''}`}>
            <div className="tm-card ts-list">
              <Invite roles={roles} onInvite={invite} busy={busy} />
              <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 8, borderBottom: `1px solid ${C.divider}` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {pickable.length > 1 && (
                    <PickBox on={allPicked} title={allPicked ? t('fleet.clearSelection') : t('fleet.selectAllShown')}
                      onToggle={() => setPicked((cur) => { const n = new Set(cur); pickable.forEach((p) => (allPicked ? n.delete(p.user_id) : n.add(p.user_id))); return n; })} />
                  )}
                  <SearchInput value={query} onChange={setQuery} placeholder={t('fleet.searchPeople')} style={{ flex: 1 }} />
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                  {chips.map((c) => {
                    const on = chip === c.id;
                    return (
                      <button key={c.id} type="button" onClick={() => setChip(c.id)} className="ts-filter"
                        style={{ background: on ? 'color-mix(in srgb, var(--om-accent) 18%, transparent)' : 'transparent', borderColor: on ? C.purple : C.borderHi, color: on ? C.text : C.sub }}>
                        {'warn' in c && <Icon name="alert-circle" size={11} color={C.purplePale} />}
                        {c.label}
                        <span style={{ color: 'warn' in c && c.n ? C.purplePale : C.muted }}>{c.n}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div style={{ padding: 6, display: 'flex', flexDirection: 'column', gap: 2 }}>
                {shown.map((p) => {
                  const on = selected?.user_id === p.user_id && !picked.size;
                  const setup = needsSetup(p);
                  const badge = badgeOf(p);
                  return (
                    <div key={p.user_id} className="cpt-hover-row tm-person ts-person" style={{ background: on ? 'var(--om-legacy-active)' : picked.has(p.user_id) ? 'color-mix(in srgb, var(--om-accent) 6%, transparent)' : 'transparent' }}>
                      <span className={`ts-person-pick${picked.size ? ' is-on' : ''}`}>
                        <PickBox on={picked.has(p.user_id)} disabled={isCo(p)} title={isCo(p) ? t('fleet.bulk.fullRoleTip') : t('fleet.bulk.pickPersonTip')}
                          onToggle={() => setPicked((cur) => { const n = new Set(cur); if (n.has(p.user_id)) n.delete(p.user_id); else n.add(p.user_id); return n; })} />
                      </span>
                      <button type="button" onClick={() => openPerson(p.user_id)} className="tm-person-open">
                        <Avatar text={initial(nameOf(p))} size={30} round />
                        <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <span className="tm-ellipsis" style={{ font: f(500, 13), color: C.text }}>
                            {dirtyPeople.has(p.user_id) && <span className="tm-dirty-dot" title={t('fleet.save.unsavedTip')} />}
                            {nameOf(p)}{p.user_id === myId ? ` · ${t('fleet.you')}` : ''}
                          </span>
                          <span className="tm-ellipsis" style={{ font: f(400, 11.5), color: setup ? C.purplePale : C.muted }}>{scopeOf(p)}</span>
                        </span>
                        {badge && <span className={`ts-role-badge${badge.custom ? ' is-custom' : ''}`} title={badge.custom ? t('ts.customTip') : undefined}>{badge.label}</span>}
                        {setup && <span title={t('ts.noRoleYet')} style={{ display: 'flex' }}><Icon name="alert-circle" size={14} color={C.purplePale} /></span>}
                        {isAgency && p.role === null && <span className="tm-role" style={{ color: C.muted }} title={t('fleet.externalTip')}>{t('fleet.external')}</span>}
                      </button>
                    </div>
                  );
                })}
                {!roster.length && <span style={{ padding: 14, font: f(400, 12), color: C.muted, textAlign: 'center' }}>{t('team.noMembersHint')}</span>}
                {roster.length > 0 && !shown.length && <span style={{ padding: 14, font: f(400, 12), color: C.muted, textAlign: 'center' }}>{t('ts.nobody')}</span>}
              </div>
              <div style={{ padding: '9px 14px', borderTop: `1px solid ${C.divider}`, font: f(400, 11), color: C.muted }}>
                {t('ts.showingPeople', { k: shown.length, n: roster.length })}
              </div>
            </div>

            <div className="tm-card ts-detail">
              {picked.size > 0 ? (
                <BulkPanel people={pickedPeople} roles={roles} copySources={copySources(picked)}
                  onSetRole={(roleId) => pickedPeople.forEach((p) => stageRoleFor(p.user_id, roleId))}
                  onCopyFrom={(from) => pickedPeople.forEach((p) => { if (p.user_id !== from) copyAccess(from, p.user_id); })}
                  onRemove={pickedPeople.every(canRemove) ? () => removePeople(pickedPeople) : null}
                  onClear={() => setPicked(new Set())}
                  onBack={() => setShowDetail(false)} />
              ) : selected ? detailFor(selected) : (
                <div style={{ padding: 32, textAlign: 'center', font: f(400, 12.5), color: C.muted }}>{t('ts.pick')}</div>
              )}
            </div>
          </div>
        )}

        {!fleetLoading && (
          <SaveBar count={dirtyCount} progress={saving} saved={justSaved} onSave={() => void save()} onDiscard={discard} />
        )}
      </div>
    </div>
  );
}

/* ── pieces ─────────────────────────────────────────────────────── */

function AgencyBar({ name, label, role, meta, accountsMenu, more, switcher }: {
  name: string; label: string; role: string | null; meta: string; accountsMenu: ReactNode; more: ReactNode; switcher: ReactNode;
}) {
  return (
    <div className="ts-agency">
      <span className="ts-agency-av">{initial(name)}</span>
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span className="tm-menu-label" style={{ padding: 0 }}>{label}</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          <span className="tm-ellipsis" style={{ font: f(700, 20, 1.15), color: C.text }}>{name}</span>
          {role && <span className="ts-role-chip">{role}</span>}
        </span>
        <span style={{ font: f(400, 12), color: C.sub }}>{meta}</span>
      </div>
      <div className="ts-agency-actions">
        {accountsMenu}
        {more}
        {switcher}
      </div>
    </div>
  );
}

function SwitchAgency({ current, agencies, showPersonal, personalMeta, onPick, onCreate }: {
  current: string | null; agencies: AgencySummary[]; showPersonal: boolean; personalMeta: string;
  onPick: (id: string) => void; onCreate: (name: string) => void;
}) {
  const { t } = useTranslation('agency');
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [draft, setDraft] = useState('');
  const close = () => { setOpen(false); setQ(''); };
  const pick = (id: string) => { close(); onPick(id); };
  const create = () => { const v = draft.trim(); if (!v) return; setDraft(''); close(); onCreate(v); };
  const rows = [
    ...(showPersonal ? [{ id: PERSONAL, name: t('fleet.personal'), meta: personalMeta, role: null as string | null }] : []),
    ...agencies.map((a) => ({ id: a.id, name: a.name, meta: `${t('fleet.memberCount', { count: a.member_count })} · ${t('fleet.accountCount', { count: a.account_count })}`, role: a.role ? t(`fleet.role.${a.role}`) : null })),
  ].filter((r) => matches(q, r.name));
  return (
    <div style={{ position: 'relative', flex: 'none' }}>
      <button type="button" onClick={() => (open ? close() : setOpen(true))} className="cpt-hover-border ts-switch-btn">
        <Icon name="repeat" size={14} color={C.purpleLt} />{t('ts.switchAgency')}
        <span className="ts-count">{agencies.length + (showPersonal ? 1 : 0)}</span>
      </button>
      <Menu open={open} onClose={close} width={320} align="right">
        <div style={{ padding: 8, borderBottom: `1px solid ${C.divider}` }}>
          <SearchInput value={q} onChange={setQ} placeholder={t('ts.findAgency')} />
        </div>
        <div className="cpt-thin-scroll" style={{ padding: 6, display: 'flex', flexDirection: 'column', gap: 2, maxHeight: 360, overflowY: 'auto' }}>
          {rows.map((r) => (
            <button key={r.id} type="button" onClick={() => pick(r.id)} className="cpt-hover-row tm-menu-row" style={{ background: current === r.id ? 'var(--om-legacy-active)' : 'transparent' }}>
              <Avatar text={initial(r.name)} size={30} />
              <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <span className="tm-ellipsis" style={{ font: f(600, 13), color: C.text }}>{r.name}</span>
                <span style={{ font: f(400, 11), color: C.muted }}>{r.meta}</span>
              </span>
              {r.role && <span className="tm-role" style={{ color: C.purpleLt }}>{r.role}</span>}
              <span style={{ width: 13, color: C.purpleLt }}>{current === r.id && <Icon name="check" size={13} />}</span>
            </button>
          ))}
          {!rows.length && <span style={{ padding: 8, font: f(400, 12), color: C.muted }}>{t('fleet.noMatches', { q: q.trim() })}</span>}
        </div>
        <div style={{ display: 'flex', gap: 6, padding: 8, borderTop: `1px solid ${C.divider}` }}>
          <input value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') create(); }}
            placeholder={t('fleet.newAgencyPlaceholder')} className="tm-input" style={{ flex: 1, height: 28 }} />
          <Btn kind="primary" onClick={create} disabled={!draft.trim()}>{t('fleet.create')}</Btn>
        </div>
      </Menu>
    </div>
  );
}

function MoreMenu({ autoPlacement, onAutoPlacement, onDelete, agencyName }: {
  autoPlacement: boolean | null; onAutoPlacement: (on: boolean) => void; onDelete: (() => void) | null; agencyName: string;
}) {
  const { t } = useTranslation('agency');
  const [open, setOpen] = useState(false);
  if (autoPlacement === null && !onDelete) return null;
  return (
    <div style={{ position: 'relative', flex: 'none' }}>
      <button type="button" onClick={() => setOpen(!open)} className="cpt-hover-border ts-switch-btn" style={{ width: 36, padding: 0, justifyContent: 'center' }} title={t('ts.more')} aria-label={t('ts.more')}>
        <Icon name="more-horizontal" size={15} color={C.sub} />
      </button>
      <Menu open={open} onClose={() => setOpen(false)} width={300} align="right">
        {autoPlacement !== null && (
          <label className="tm-menu-row" style={{ cursor: 'pointer', alignItems: 'flex-start', padding: '10px 12px' }}>
            <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <span style={{ font: f(500, 12.5), color: C.text }}>{t('fleet.autoPlacement')}</span>
              <span style={{ font: f(400, 11), color: C.muted }}>{t('fleet.autoPlacementTip')}</span>
            </span>
            <Toggle on={autoPlacement} onChange={onAutoPlacement} title={t('fleet.autoPlacementTip')} />
          </label>
        )}
        {onDelete && (
          <div style={{ padding: 6, borderTop: autoPlacement !== null ? `1px solid ${C.divider}` : 0 }}>
            <button type="button" onClick={() => { setOpen(false); onDelete(); }} className="cpt-hover-row tm-menu-row" style={{ color: C.sub, font: f(500, 12), height: 30 }}>
              <Icon name="trash-2" size={12} />{t('fleet.deleteAgency', { name: agencyName })}
            </button>
          </div>
        )}
      </Menu>
    </div>
  );
}

function Invite({ roles, busy, onInvite }: { roles: TeamRole[]; busy: boolean; onInvite: (identifier: string, roleId: string) => Promise<void> }) {
  const { t } = useTranslation('agency');
  const [value, setValue] = useState('');
  const [roleId, setRoleId] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const fallback = roles.find((r) => r.key === 'chatter')?.id ?? roles.find((r) => !r.locked)?.id ?? NONE;
  const current = roleId && (roleId === NONE || roles.some((r) => r.id === roleId)) ? roleId : fallback;
  const options = [...roles.map((r) => ({ id: r.id, name: r.name, desc: r.description })), { id: NONE, name: t('ts.noAccess'), desc: t('ts.noAccessDesc') }];
  const go = async () => { const v = value.trim(); if (!v || busy) return; await onInvite(v, current); setValue(''); };
  return (
    <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 8, borderBottom: `1px solid ${C.divider}` }}>
      <span style={{ font: f(600, 12), color: C.text }}>{t('ts.invite')}</span>
      <div style={{ display: 'flex', gap: 6 }}>
        <input value={value} onChange={(e) => setValue(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') void go(); }}
          placeholder={t('team.addPlaceholder')} title={t('team.addHint')} className="tm-input" style={{ flex: 1, height: 30 }} />
        <div style={{ position: 'relative', flex: 'none' }}>
          <button type="button" onClick={() => setOpen(!open)} title={t('ts.inviteRoleTip')} className="cpt-hover-border ts-mini-select">
            <span className="tm-ellipsis" style={{ maxWidth: 90 }}>{options.find((o) => o.id === current)?.name}</span>
            <Icon name="chevron-down" size={12} color={C.muted} />
          </button>
          <Menu open={open} onClose={() => setOpen(false)} width={240} align="right">
            <div style={{ padding: 4, display: 'flex', flexDirection: 'column', gap: 1 }}>
              {options.map((o) => (
                <button key={o.id} type="button" className="cpt-hover-row tm-menu-row" onClick={() => { setRoleId(o.id); setOpen(false); }}>
                  <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                    <span style={{ font: f(500, 12.5), color: C.text }}>{o.name}</span>
                    <span className="tm-ellipsis" style={{ font: f(400, 11), color: C.muted }}>{o.desc}</span>
                  </span>
                  <span style={{ width: 13, color: C.purpleLt }}>{o.id === current && <Icon name="check" size={13} />}</span>
                </button>
              ))}
            </div>
          </Menu>
        </div>
        <Btn kind="primary" height={30} disabled={!value.trim() || busy} onClick={() => void go()}>{t('ts.inviteBtn')}</Btn>
      </div>
      <span style={{ font: f(400, 11), color: C.muted }}>{t('ts.inviteHint')}</span>
    </div>
  );
}

/** The person's Agency role, as a pill that opens what the caller may change. */
function AgencyRoleControl({ person, myId, myRole, canGovern, onRole, onMakeMember, onMakeOwner }: {
  person: Person; myId: string; myRole: AgencyRole | null; canGovern: boolean;
  onRole: (r: AgencyRole) => void; onMakeMember: () => void; onMakeOwner: () => void;
}) {
  const { t } = useTranslation('agency');
  const [open, setOpen] = useState(false);
  const label = person.role ? t(`fleet.role.${person.role}`) : t('fleet.external');
  const isCoManager = myRole === 'owner' || myRole === 'admin' || canGovern;
  const options: { id: string; label: string; tip: string; act: () => void; on?: boolean }[] = [];
  if (person.role == null && isCoManager) options.push({ id: 'member', label: t('fleet.makeMember'), tip: t('fleet.memberTip'), act: onMakeMember });
  if ((person.role === 'admin' || person.role === 'member') && canGovern) {
    options.push({ id: 'admin', label: t('fleet.role.admin'), tip: t('fleet.adminTip'), act: () => onRole('admin'), on: person.role === 'admin' });
    options.push({ id: 'member', label: t('fleet.role.member'), tip: t('fleet.memberTip'), act: () => onRole('member'), on: person.role === 'member' });
    if (person.user_id !== myId) options.push({ id: 'owner', label: t('fleet.makeOwner'), tip: '', act: onMakeOwner });
  }
  const pill = <span className="ts-role-chip" style={{ color: person.role ? C.purplePale : C.muted }} title={person.role ? undefined : t('fleet.externalTip')}>{label}</span>;
  if (!options.length) return pill;
  return (
    <span style={{ position: 'relative', display: 'flex' }}>
      <button type="button" onClick={() => setOpen(!open)} className="ts-role-btn">{pill}<Icon name="chevron-down" size={11} color={C.muted} /></button>
      <Menu open={open} onClose={() => setOpen(false)} width={240}>
        <div style={{ padding: 4, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {options.map((o) => (
            <button key={o.id} type="button" className="cpt-hover-row tm-menu-row" onClick={() => { setOpen(false); o.act(); }}>
              <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                <span style={{ font: f(500, 12.5), color: C.text }}>{o.label}</span>
                {o.tip && <span className="tm-ellipsis" style={{ font: f(400, 11), color: C.muted }}>{o.tip}</span>}
              </span>
              <span style={{ width: 13, color: C.purpleLt }}>{o.on && <Icon name="check" size={13} />}</span>
            </button>
          ))}
        </div>
      </Menu>
    </span>
  );
}

function BulkPanel({ people, roles, copySources, onSetRole, onCopyFrom, onRemove, onClear, onBack }: {
  people: Person[]; roles: TeamRole[]; copySources: CopySource[];
  onSetRole: (roleId: string) => void; onCopyFrom: (uid: string) => void; onRemove: (() => void) | null; onClear: () => void; onBack: () => void;
}) {
  const { t } = useTranslation('agency');
  const [dd, setDd] = useState<'role' | 'cp' | null>(null);
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <div className="ts-detail-head">
        <button type="button" className="cpt-hover-icon tm-x ts-back" onClick={onBack} title={t('ts.back')}><Icon name="arrow-left" size={15} /></button>
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={{ font: f(600, 15), color: C.text }}>{t('ts.selected', { count: people.length })}</span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {people.map((p) => (
              <span key={p.user_id} className="tm-chip-sm"><Avatar text={initial(nameOf(p))} size={18} round /><span className="tm-ellipsis" style={{ maxWidth: 160, paddingRight: 6 }}>{nameOf(p)}</span></span>
            ))}
          </div>
        </div>
        <Btn kind="muted" onClick={onClear}>{t('fleet.clearSelection')}</Btn>
      </div>
      <div className="ts-step-body">
        <span style={{ font: f(400, 12), color: C.sub }}>{t('ts.bulkHint')}</span>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          <div style={{ position: 'relative' }}>
            <Btn kind="tint" icon="shield" onClick={() => setDd(dd === 'role' ? null : 'role')}>{t('ts.setRole')}</Btn>
            <Menu open={dd === 'role'} onClose={() => setDd(null)} width={240}>
              <div style={{ padding: 4, display: 'flex', flexDirection: 'column', gap: 1 }}>
                {[...roles.map((r) => ({ id: r.id, name: r.name, desc: r.description })), { id: NONE, name: t('ts.noAccess'), desc: t('ts.noAccessDesc') }].map((o) => (
                  <button key={o.id} type="button" className="cpt-hover-row tm-menu-row" onClick={() => { setDd(null); onSetRole(o.id); }}>
                    <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                      <span style={{ font: f(500, 12.5), color: C.text }}>{o.name}</span>
                      <span className="tm-ellipsis" style={{ font: f(400, 11), color: C.muted }}>{o.desc}</span>
                    </span>
                  </button>
                ))}
              </div>
            </Menu>
          </div>
          <div style={{ position: 'relative' }}>
            <Btn kind="ghost" icon="copy" disabled={!copySources.length} onClick={() => setDd(dd === 'cp' ? null : 'cp')}>{t('ts.copyFrom')}</Btn>
            <Menu open={dd === 'cp'} onClose={() => setDd(null)} width={300}>
              <span style={{ padding: '8px 10px 4px', font: f(400, 11), color: C.muted }}>{t('ts.copyHint')}</span>
              <div className="cpt-thin-scroll" style={{ padding: 4, display: 'flex', flexDirection: 'column', gap: 1, maxHeight: 320, overflowY: 'auto' }}>
                {copySources.map((s) => (
                  <button key={s.user_id} type="button" className="cpt-hover-row tm-menu-row" onClick={() => { setDd(null); onCopyFrom(s.user_id); }}>
                    <Avatar text={initial(s.name)} size={24} round />
                    <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                      <span className="tm-ellipsis" style={{ font: f(500, 12.5), color: C.text }}>{s.name}</span>
                      <span className="tm-ellipsis" style={{ font: f(400, 11), color: C.muted }}>{s.sub}</span>
                    </span>
                  </button>
                ))}
              </div>
            </Menu>
          </div>
          {onRemove && <Btn kind="muted" icon="user-minus" onClick={onRemove}>{t('ts.removeN', { count: people.length })}</Btn>}
        </div>
      </div>
    </div>
  );
}
